import { FC } from 'react'
import { toast } from '@wellesley/system-toast'
import { useForm } from 'react-hook-form'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { getErrorMsg } from '@wellesley/error-kit'
import { usePostApiV1ReportsMutation } from '@wellesley/openapi'
import { Form, Textarea, type UploadFileProps } from '@wellesley/ui'

import ReportLayout from '../ReportLayout'
import { getReportOptions } from '../ReportList/options'

import FileUpload from './FileUpload'
import SelectedOptionHeader from './SelectedOptionHeader'
import { type TCommonReport } from './types'

const CommonReport: FC<TCommonReport> = ({
  groupId,
  currentStep,
  targetReportEntityId,
  onSuccess,
}) => {
  const form = useForm<{ text: string; files: UploadFileProps[] }>({
    defaultValues: { text: '', files: [] },
  })
  const [makeReport, { isLoading }] = usePostApiV1ReportsMutation()

  const stepOption = getReportOptions(true).find((o) => o.step === currentStep)

  const onSubmit = async () => {
    const { text } = form.getValues()

    try {
      await makeReport({
        createReport: {
          comment: text,
          targetId: targetReportEntityId,
          forward: true,
          groupId,
          uploadIds: [], // TODO: upload files to server, get uploadIds, and attach to report
          type: stepOption?.type ?? 'OTHER',
        },
      }).unwrap()
      onSuccess()
    } catch (error) {
      toast.error(getErrorMsg(error as FetchBaseQueryError) ?? '')
    }
  }

  return (
    <ReportLayout
      isLoading={isLoading}
      onRightButtonClick={() => void onSubmit()}
      rightButtonTitle="Report"
    >
      <Form {...form}>
        <div className="gap-m flex flex-col">
          {stepOption && <SelectedOptionHeader {...stepOption} />}

          <div className="gap-xxs flex flex-col">
            <label className="text-sm font-medium text-gray-700">
              Details (optional)
            </label>
            <Textarea
              value={form.watch('text')}
              onChange={(e) => void form.setValue('text', e.target.value)}
              placeholder="Describe the issue in detail"
              className="min-h-[128px] resize-none"
            />
          </div>

          <FileUpload
            files={form.watch('files')}
            onFilesChange={(files) => form.setValue('files', files)}
          />
        </div>
      </Form>
    </ReportLayout>
  )
}

export default CommonReport
